import PersonIcon from "@mui/icons-material/Person";
import {
  AppBar,
  AppBarProps,
  Avatar,
  Box,
  ButtonBase,
  LinearProgress,
  Toolbar,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import NextLink from "next/link";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "src/hooks/use-auth";
import { getUserDetails } from "src/slices/articles/thunks";
import { getActivePlan } from "src/slices/auth/thunks";
import { selectCurrentBrief } from "src/slices/brief";
import { locations } from "src/slices/brief/helpers";
import { getProjects, projects } from "src/slices/projects";
import { setUserRole, tabSelected } from "src/slices/system";
import {
  selectCurrentPlan,
  selectLastSaved,
} from "src/slices/system/selectors";
import { getPlans } from "src/slices/system/thunks";
import { TabType } from "src/slices/system/types";
import { useDispatch } from "src/store";
import { Logo } from "../../components/logo";
import { AccountPopover } from "./account-popover";

interface DashboardNavbarProps extends AppBarProps {
  onOpenSidebar?: () => void;
}

const DashboardNavbarRoot = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  ...(theme.palette.mode === "light"
    ? {
        boxShadow: theme.shadows[3],
      }
    : {
        backgroundColor: theme.palette.background.paper,
        borderBottomColor: theme.palette.divider,
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
        boxShadow: "none",
      }),
}));

const AccountButton = () => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const { user, logout } = useAuth();
  const dispatch = useDispatch();

  const handleOpenPopover = (): void => {
    setOpenPopover(true);
  };

  const handleClosePopover = (): void => {
    setOpenPopover(false);
  };

  return (
    <>
      <Box
        component={ButtonBase}
        onClick={handleOpenPopover}
        ref={anchorRef}
        sx={{
          alignItems: "center",
          display: "flex",
          ml: 1,
        }}
      >
        <Avatar
          sx={{
            height: 40,
            width: 40,
            backgroundColor: "#434080",
          }}
          src={user.avatar}
        >
          <PersonIcon />
        </Avatar>
      </Box>
      <AccountPopover
        anchorEl={anchorRef.current}
        onClose={handleClosePopover}
        open={openPopover}
      />
    </>
  );
};

export const DashboardNavbar: FC<DashboardNavbarProps> = (props) => {
  const { onOpenSidebar, ...other } = props;

  const dispatch = useDispatch();

  const handleChange = (event: React.ChangeEvent<{}>, value: TabType): void => {
    dispatch(tabSelected(value));
  };

  const getLocationFlag = (name: string) => {
    return (
      locations
        .find((location) => location.location_name === name)
        ?.country_iso_code?.toLowerCase() ?? "us"
    );
  };
  const [offset, setOffset] = useState(0);
  const [stickedToTop, setStickedToTop] = useState(false);
  const brief = useSelector(selectCurrentBrief);
  const lastSaved = useSelector(selectLastSaved);
  const { isAuthenticated, user: authUser, logout } = useAuth();
  const userProjects = useSelector(projects);
  const [numberOfProjects, setNumberOfProjects] = useState(0);
  const currentPlan = useSelector(selectCurrentPlan);

  const router = useRouter();
  const isBrief = router.pathname.includes("/brief");
  const isProject = router.pathname == "/projects";
  const isDashboard = router.pathname == "/dashboard";
  const isSeoMissions = router.pathname == "/seoMissions";
  const isContentUpdate = router.pathname == "/contentUpdate";
  const isHumanizer = router.pathname == "/humanizer";

  const isProduction = window.location.hostname.includes("seowind.io");

  const getProjectLimit = async () => {
    const currentPlan = (await dispatch(getActivePlan())) as any;
    if (currentPlan?.payload?.plan?.numberOfProjects) {
      setNumberOfProjects(currentPlan?.payload?.plan?.numberOfProjects);
    } else if (authUser?.isOnboardingShown) {
      setNumberOfProjects(1);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getPlans());
      dispatch(getProjects());
      getProjectLimit();
    }

    const onScroll = () => setOffset(window.pageYOffset);
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (offset > 16 && !stickedToTop) {
      setStickedToTop(true);
    }
    if (offset == 0) {
      setStickedToTop(false);
    }
  }, [offset]);

  const getUserRole = async () => {
    let result = (await dispatch(getUserDetails())).payload?.user;
    if (result === undefined) {
      logout();
      router.push("/authentication/login");
      return;
    }
    dispatch(setUserRole(result?.role));
  };

  useEffect(() => {
    getUserRole();
  }, []);

  useEffect(() => {
    getUserRole();
  }, [router.pathname]);

  return (
    <Box
      sx={{
        paddingBottom: "16px",
        paddingLeft: "0px",
        paddingRight: "0px",
        // marginTop: "16px",
        position: "sticky",
        width: "100%",
        zIndex: 99999,
        top: 0,
        transition: "all 0.3s ease-in-out",
      }}
    >
      <DashboardNavbarRoot
        {...other}
        sx={{
          // borderRadius: stickedToTop ? "0rem" : "0.5rem",
          position: "static",
          backgroundColor: "#080548",
          height: "72px",
          paddingLeft: stickedToTop ? "16px" : "0px",
          paddingRight: stickedToTop ? "16px" : "0px",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            left: 0,
            px: 2,
            height: "72px",
          }}
        >
          <NextLink href="/dashboard" passHref>
            <a>
              <Logo
                sx={{
                  height: 31,
                  width: 150,
                }}
                variant="light"
              />
            </a>
          </NextLink>
          {/* projects commented out */}
          <NextLink href="/dashboard" passHref>
            <Typography
              color="#EEF3FD"
              fontFamily="Inter"
              fontWeight="600"
              fontSize="16px"
              borderBottom={isDashboard ? "2px solid #5048E5" : "none"}
              paddingBottom="4px"
              sx={{
                display: "flex",
                alignItems: "center",
                ml: 5,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              New Articles
            </Typography>
          </NextLink>

          {/* Content update */}

          <NextLink href="/contentUpdate" passHref>
            <Typography
              color="#EEF3FD"
              fontFamily="Inter"
              fontWeight="600"
              fontSize="16px"
              borderBottom={isContentUpdate ? "2px solid #5048E5" : "none"}
              paddingBottom="4px"
              sx={{
                display: "flex",
                alignItems: "center",
                ml: 3,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Updated Articles
            </Typography>
          </NextLink>

          {/* seo missions */}

          <NextLink href="/seoMissions" passHref>
            <Typography
              color="#EEF3FD"
              fontFamily="Inter"
              fontWeight="600"
              fontSize="16px"
              borderBottom={isSeoMissions ? "2px solid #5048E5" : "none"}
              paddingBottom="4px"
              sx={{
                display: "flex",
                alignItems: "center",
                ml: 3,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              SEO missions
            </Typography>
          </NextLink>

          {/* projects commented out */}
          <NextLink href="/projects" passHref>
            <Typography
              color="#EEF3FD"
              fontFamily="Inter"
              fontWeight="600"
              fontSize="16px"
              borderBottom={isProject ? "2px solid #5048E5" : "none"}
              paddingBottom="4px"
              sx={{
                display: "flex",
                alignItems: "center",
                ml: 3,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Projects
            </Typography>
          </NextLink>

          {/* humanizer */}
          <NextLink href="/humanizer" passHref>
            <Typography
              color="#EEF3FD"
              fontFamily="Inter"
              fontWeight="600"
              fontSize="16px"
              borderBottom={isHumanizer ? "2px solid #5048E5" : "none"}
              paddingBottom="4px"
              sx={{
                display: "flex",
                alignItems: "center",
                ml: 3,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              AI Humanizer
            </Typography>
          </NextLink>

          {/* <IconButton
            onClick={onOpenSidebar}
            sx={{
              display: {
                xs: "inline-flex",
                lg: "none",
              },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton> */}

          <Box
            sx={{ display: "flex", flex: 1, justifyContent: "space-between" }}
          >
            {isBrief && brief?.keyword && (
              <>
                <Box sx={{ alignSelf: "end" }}>
                  {/* <TabList
                  //   onChange={handleChange}
                  //   aria-label="lab API tabs example"
                  //   textColor="inherit"
                  //   sx={{ pl: 6 }}
                  // >
                  //   <Tab
                  //     label="Brief"
                  //     value="brief"
                  //     sx={{ fontSize: "16px", fontWeight: "bold" }}
                  //   />
                  //   {isAuthenticated && !isProduction && (
                  //     <Tab
                  //       label="Content Grader"
                  //       value="grader"
                  //       sx={{ fontSize: "16px", fontWeight: "bold" }}
                  //     />
                  //   )}
                  // </TabList> */}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", pr: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body1"
                        color="white"
                        sx={{ pr: 1, fontWeight: "500" }}
                      >
                        Brief for:
                      </Typography>
                      <Typography variant="h6" color="white">
                        {brief?.isSample
                          ? `[Sample] ${brief.keyword}`
                          : brief.keyword}
                      </Typography>
                      <Box
                        sx={{
                          marginLeft: "0.5rem",
                          width: "30px",
                        }}
                      >
                        <span
                          className={`fi fi-${getLocationFlag(
                            brief.locationName
                          )}`}
                        ></span>
                      </Box>
                    </Box>
                    {lastSaved && (
                      <Box
                        sx={{
                          color: "white",
                          fontSize: "12px",
                        }}
                      >
                        last saved:{" "}
                        {format(new Date(lastSaved), "MMM dd, h:mm a")}
                      </Box>
                    )}
                  </Box>
                  {/* <Share /> */}
                </Box>
              </>
            )}
          </Box>
          {/* projects commented out */}
          {!isBrief && !brief?.keyword && (
            <>
              <Typography
                variant="body1"
                color="#fff"
                sx={{
                  pr: 1,
                  fontWeight: "500",
                  fontFamily: "Inter",
                  fontSize: "14px",
                }}
              >
                Projects:
              </Typography>

              <Box sx={{ width: "69px", mr: 2 }}>
                <LinearProgress
                  variant="determinate"
                  sx={{
                    backgroundColor: "rgba(238, 243, 253, 0.1)",
                    borderRadius: "3px",
                    height: "4px",
                    "& .MuiLinearProgress-bar": {
                      background:
                        "linear-gradient(60.66deg, #FFD875 -3.87%, #DA00C8 26.08%, #6A00FF 56.49%)",
                    },
                  }}
                  value={
                    (userProjects?.length / numberOfProjects) * 100 > 100
                      ? 100
                      : (userProjects?.length / numberOfProjects) * 100
                  }
                />
              </Box>
              <Box
                minWidth={35}
                sx={{
                  mr: 2,
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {userProjects?.length} / {numberOfProjects}
                </Typography>
              </Box>
            </>
          )}

          {isAuthenticated && <AccountButton />}
        </Toolbar>
      </DashboardNavbarRoot>
    </Box>
  );
};

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func,
};
